import { atom, useAtomValue, useSetAtom } from 'jotai'
import { loadable } from 'jotai/utils'
import { indexDailyStatFactory } from 'services/query-v2/products/index/daily-statistics'
import { indexDataFactory } from 'services/query-v2/products/index/product-info'

import Big from 'big.js'
import { StateConfig } from 'components/charts/config'
import { AnyWritableAtom } from 'components/scope-provider'
import { productAssetsFactory, ProductMetadata } from 'indices-list'
import { ProductType } from 'sdk'
import { apyRequestFactory } from 'services/query-v2/products/apy'
import { defaultProduct } from 'services/query-v2/products/index/product-info/model'
import {
  defaultDailyStats,
  ProductStorage,
} from '../../../services/query-v2/products/index/daily-statistics/model'
import { UnsupportedProductTypeError } from '../../../utils/errors'
import { SwitchElement } from '../component/switch-sections/interface'
import { ProductActionType } from './interface'
import { CAI } from 'products'

// INFO: INDEX METADATA ATOM
export const productMetadataAtom = atom(productAssetsFactory(CAI))

// INFO: DISPATCH ATOMS
const timeIntervalAtom = atom<StateConfig>({ name: '1 year', range: 31536000 })
const productPreviewAtom = atom<SwitchElement>(SwitchElement.PERFORMANCE)
const productDispatchAtom = atom(null, (_, set, action: ProductActionType) => {
  switch (action.type) {
    case 'set_chart_time_window':
      set(timeIntervalAtom, action.payload)
      break
    case 'set_selected_product_preview':
      set(productPreviewAtom, action.payload)
      break
    default:
      break
  }
})

// INFO: APY ATOMS
const apyAtoms = apyRequestFactory()
const loadableApyRequestAtom = loadable(apyAtoms.response)

// INFO: PRODUCT ATOMS
const productAtoms = indexDataFactory()
const loadableProductRequestAtom = loadable(productAtoms.response)

// INFO: DAILY STATS ATOMS
const dailyStatRequestAtoms = indexDailyStatFactory()
const loadableIndexDailyStatsAtom = loadable(dailyStatRequestAtoms.response)

export const dailyStatsAtom = atom(async (get) => {
  const loadableAtom = await get(loadableIndexDailyStatsAtom)

  if (loadableAtom.state === 'hasError') {
    throw loadableAtom.error
  }

  if (loadableAtom.state === 'hasData') {
    return loadableAtom.data
  }
  return defaultDailyStats
})

export const productApyAtom = atom(async (get) => {
  const loadableAtom = await get(loadableApyRequestAtom)
  if (loadableAtom.state === 'hasError') {
    throw loadableAtom.error
  }
  if (loadableAtom.state === 'hasData') {
    return loadableAtom.data
  }
  return { apy: Big(0) }
})

export const productAtom = atom(async (get) => {
  const loadableAtom = await get(loadableProductRequestAtom)

  if (loadableAtom.state === 'hasError') {
    throw loadableAtom.error
  }

  if (loadableAtom.state === 'hasData') {
    return loadableAtom.data
  }
  return defaultProduct
})

const isErrorAtom = atom(async (get) => {
  const [loadableAtom1, loadableAtom2, loadableAtom3] = await Promise.all([
    get(loadableIndexDailyStatsAtom),
    get(loadableApyRequestAtom),
    get(loadableProductRequestAtom),
  ])

  const isError =
    loadableAtom1.state === 'hasError' ||
    loadableAtom2.state === 'hasError' ||
    loadableAtom3.state === 'hasError'

  return isError
})

export const createProductStorage = (productType: ProductType): ProductStorage => {
  switch (productType) {
    case ProductType.INDEX_V1:
    case ProductType.INDEX_V2:
      return {
        apy: useAtomValue(productApyAtom),
        product: useAtomValue(productAtom),
        dailyStats: useAtomValue(dailyStatsAtom),
        timeWindow: useAtomValue(timeIntervalAtom),
        productPreviewSelection: useAtomValue(productPreviewAtom),
        dispatch: useSetAtom(productDispatchAtom),
        isError: useAtomValue(isErrorAtom),
      }
    default: {
      throw new UnsupportedProductTypeError(productType)
    }
  }
}

export const createProductStorageAtoms = (
  product: ProductMetadata,
): [AnyWritableAtom, unknown][] => {
  switch (product.productType) {
    case ProductType.INDEX_V1:
    case ProductType.INDEX_V2:
      //INFO: to switch scenario bind another factory to foreground and background atoms
      return [
        //METADATA
        [productMetadataAtom, product],
        //PRODUCT
        [productAtoms.productAddress, product.address],
        [productAtoms.chainId, product.network],
        //APY
        [apyAtoms.productAddress, product.address],
        [apyAtoms.chainId, product.network],
        //STATS
        [dailyStatRequestAtoms.productAddress, product.address],
        [dailyStatRequestAtoms.chainId, product.network],
      ]
    default:
      throw new UnsupportedProductTypeError(product.productType)
  }
}
